'use client';

import React, { useContext, FC, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { LFAsyncSelect, LFPlainSelect } from '@app/_components/form-control/selects';
import { createAsyncOptionLoader } from '@app/_components/form-control/selects/createAsyncOptionLoader';
import { RootStoreContext } from '@stores/rootStore';
import { LoadOptions } from 'react-select-async-paginate';
import { GroupBase } from 'react-select';
import { ISeason } from '@stores/SeasonStore';
import { useActiveSeason } from '../../overview/_components/header/hooks/useActiveSeason';
import { useActiveYear } from '../../overview/_components/header/hooks/useActiveYear';
import { isPresent } from '@farmai-shared/LFUtils';
import { useLFSearchParams } from '@services/useLFSearchParams';
import Cookies from 'js-cookie';
const CATEGORY_OPTIONS = [{
  label: 'season',
  value: 'season_id'
}, {
  label: 'year',
  value: 'year'
}];
export const SeasonYearFilterDropdown: FC = observer(() => {
  const {
    uiStore
  } = useContext(RootStoreContext);
  const {
    defaultSeason,
    activeSeason,
    seasons
  } = useActiveSeason();
  const {
    yearParam,
    defaultYear,
    defaultYearDisplay,
    activeYear,
    activeYearDisplay
  } = useActiveYear();
  const {
    setParams
  } = useLFSearchParams();
  const [category, setCategory] = useState(yearParam ? CATEGORY_OPTIONS[1] : CATEGORY_OPTIONS[0]);
  const [selectedOption, setSelectedOption] = useState<{
    label: string;
    value: string;
  }>(yearParam || !isPresent(activeSeason) ? {
    label: activeYearDisplay,
    value: activeYear
  } : {
    label: activeSeason.name,
    value: activeSeason.id
  });
  const loadSeasonOptions = useMemo(() => createAsyncOptionLoader<ISeason, {
    label: string;
    value: string;
  }>(({
    page
  }) => uiStore.currentOrganization!.fetchSeasons(page), o => ({
    label: o.name,
    value: o.id
  })), []);
  const loadYearOptions: LoadOptions<{
    label: string;
    value: string;
  }, GroupBase<{
    label: string;
    value: string;
  }>, {
    page: number;
  }> = async (_search, _prevOptions, _extra) => ({
    options: uiStore.currentOrganization?.yearList || [],
    hasMore: false,
    additional: {
      page: 1
    }
  });
  const handleCategoryChange = useCallback((option: {
    label: string;
    value: string;
  }) => {
    Cookies.set('selectedDefaultSeasonYearDropdown', option.value);
    setCategory(option);
    if (option.value === 'year') {
      setSelectedOption({
        label: defaultYearDisplay,
        value: defaultYear
      });
      setParams([{
        key: 'filter[season_id]',
        value: null
      }, {
        key: 'filter[year]',
        value: defaultYear
      }]);
    } else {
      if (!isPresent(defaultSeason)) return;
      setSelectedOption({
        label: defaultSeason.name,
        value: defaultSeason.id
      });
      setParams([{
        key: 'filter[year]',
        value: null
      }, {
        key: 'filter[season_id]',
        value: defaultSeason.id
      }]);
    }
  }, [setParams, defaultYear, defaultYearDisplay, defaultSeason]);
  const handleValueChange = useCallback((option: {
    label: string;
    value: string;
  } | null) => {
    if (!isPresent(option)) return;
    setSelectedOption(option);
    if (category.value === 'year') {
      setParams([{
        key: 'filter[season_id]',
        value: null
      }, {
        key: 'filter[year]',
        value: option.value
      }]);
    } else {
      setParams([{
        key: 'filter[year]',
        value: null
      }, {
        key: 'filter[season_id]',
        value: option.value
      }]);
    }
  }, [setParams, category.value]);
  return <div className="w-full grow">
      <div className={`
          flex w-full flex-row rounded-lg border border-silver
          focus-within:border-lf-navy
        `}>
        <div className="flex-none">
          <LFPlainSelect id="multiselect" options={CATEGORY_OPTIONS} onChange={handleCategoryChange} value={category} withDefault={false} disabled={!isPresent(seasons)} customDropdownStyles={defaultStyles => ({
          ...defaultStyles,
          control: provided => ({
            ...provided,
            border: 0,
            height: '100%',
            background: '#E5E9F3',
            boxShadow: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }),
          valueContainer: provided => ({
            ...provided,
            fontSize: `1.25rem`,
            border: 'none',
            color: '#1C1F28',
            padding: '0px',
            paddingLeft: '8px'
          }),
          singleValue: provided => ({
            ...provided,
            fontSize: `1.25rem`,
            color: '#727D9F',
            width: `auto`,
            fontWeight: 'lighter'
          }),
          indicatorsContainer: provided => ({
            ...provided,
            marginTop: '0',
            width: 'fit'
          }),
          menu: provided => ({
            ...provided,
            padding: '1px'
          })
        })} />
        </div>
        <div className="flex-1">
          <LFAsyncSelect id={category.value} value={selectedOption} testId="overview-season-year-filter" loadOptions={category.value === 'year' ? loadYearOptions : loadSeasonOptions} onChange={handleValueChange} hideSelectedOptions={false} cacheUniqs={[category.value, uiStore.currentOrganization?.id ?? '', uiStore.currentOrganization?.createdAt ?? 0]} />
        </div>
      </div>
    </div>;
});